"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Clock, Calendar, ArrowRight, Loader } from "lucide-react";
import { PostsProvider, usePosts, Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
function PostsConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  if (loading && !isRefetching) {
    return <div data-section-id="224654" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="h-[400px] animate-pulse overflow-hidden rounded-2xl bg-muted/30 border-0" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground mb-4 font-default">Unable to load automation recipes at this time.</p>
        <Button variant="outline" className="rounded-full border-primary" onClick={() => window.location.reload()}>Try Again</Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="py-12 text-center">
        <p className="text-muted-foreground font-default">No smart home guides available at the moment.</p>
      </div>;
  }
  const postsByCategory = posts.reduce((acc: Record<string, Post[]>, post) => {
    const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : "Uncategorized";
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(post);
    return acc;
  }, {});
  const categories = Object.keys(postsByCategory);
  return <div className="space-y-16">
      {categories.map((categoryName, catIdx) => <div key={categoryName} data-index={catIdx} className="space-y-8">
          <h2 className="font-default text-2xl font-bold tracking-tight text-primary md:text-3xl border-l-4 border-primary pl-4">
            {categoryName}
          </h2>
          
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {postsByCategory[categoryName].map((post: Post, i: number) => <div key={post.id} data-index={i} className="group relative h-[450px] w-full overflow-hidden rounded-3xl shadow-sm transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
                <Link to={post.link || "#"} className="absolute inset-0 z-20">
                  <span className="sr-only">Read {post.title}</span>
                </Link>
                
                <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/smart_home_minimalist_interior.png"} alt={post.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-opacity duration-300" />

                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                  <div className="transform transition-transform duration-500 group-hover:-translate-y-2">
                    <div className="mb-4 flex flex-wrap items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-white/90">
                      {post.date && <div className="flex items-center gap-1.5">
                          <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                          <span>{new Date(post.date).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}</span>
                        </div>}
                      <div className="flex items-center gap-1.5">
                        <Clock className="size-3.5" />
                        <span>5 min read</span>
                      </div>
                      {post.categories && post.categories.length > 0 && <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-white backdrop-blur-md border border-white/20">
                          {post.categories[0].name}
                        </span>}
                    </div>

                    <h3 className="font-default mb-3 text-2xl font-bold leading-tight text-white md:text-3xl tracking-tight">
                      {post.title}
                    </h3>

                    {post.excerpt && <div className="mb-6 line-clamp-2 text-sm text-white/80 opacity-0 transition-opacity duration-500 group-hover:opacity-100 md:opacity-100 font-default" dangerouslySetInnerHTML={{
                __html: post.excerpt
              }} />}

                    <div className="flex items-center text-xs font-bold uppercase tracking-widest text-white opacity-0 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-2">
                      <span>View Recipe</span>
                      <ArrowRight className="ml-2 size-4" />
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>)}

      {hasNext && <div className="flex justify-center pt-8">
          <Button onClick={async () => await loadMore()} disabled={isRefetching} size="lg" variant="outline" className="min-w-[200px] rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors font-default">
            {isRefetching ? <>
                <Loader className="mr-2 size-4 animate-spin" />
                Syncing...
              </> : "Load More Recipes"}
          </Button>
        </div>}
    </div>;
}
export default function LArteDellaCattura() {
  const wp_query_params = {
    post_type: "post",
    per_page: 12,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="all-recipes" className="bg-background text-foreground py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 md:mb-16 lg:mb-20 max-w-3xl">
          <h2 className="font-default text-3xl font-bold tracking-tighter text-primary sm:text-4xl md:text-5xl lg:text-6xl mb-6">Automation Remix<br /></h2>
          <p className="text-lg text-muted-foreground md:text-xl leading-relaxed font-default">
            Discover the art of simple living through curated HomeKit automations. From morning rituals to evening security, explore our technical guides designed for the modern, minimalist home.
          </p>
        </div>
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 12,
        order: "desc",
        orderby: "date"
      }}>
          <PostsConsumer />
        </PostsProvider>
      </div>
    </section>;
}